import { useState } from "react";
import { toast } from "sonner";
import { Plus, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppointmentModal } from "@/components/AppointmentModal";
import { ReminderMessageDialog } from "@/components/ReminderMessageDialog";
import { AppointmentCard } from "@/components/AppointmentCard";
import { useAppState } from "@/context/AppState";
import { isSameDay, addDays, formatDiaFecha, computeRisk } from "@/lib/agenda";

export default function AgendaView() {
  const {
    appointments,
    therapists,
    addAppointment,
    updateAppointment,
    deleteAppointment,
  } = useAppState();
  const [tab, setTab] = useState("hoy");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reminder, setReminder] = useState(null);
  const today = new Date();

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (appt) => {
    setEditing(appt);
    setModalOpen(true);
  };

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await updateAppointment(editing.id, payload);
        toast.success("Cita actualizada.");
        setModalOpen(false);
      } else {
        const created = await addAppointment(payload);
        setModalOpen(false);
        toast.success("Listo. Tu cita está guardada.");
        setReminder(created);
      }
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAppointment(id);
      toast.success("Cita eliminada.");
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const togglePayment = async (appt) => {
    try {
      await updateAppointment(appt.id, {
        payment_status: appt.payment_status === "pagado" ? "pendiente" : "pagado",
      });
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const toggleConfirmed = async (appt) => {
    try {
      await updateAppointment(appt.id, { reminder_confirmed: !appt.reminder_confirmed });
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const toggleNoShow = async (appt) => {
    try {
      await updateAppointment(appt.id, { no_show: !appt.no_show });
    } catch (e) {
      toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde.");
    }
  };

  const openReminder = (appt) => setReminder(appt);

  const markSent = async (appt) => {
    if (appt.reminder_sent) return;
    try {
      await updateAppointment(appt.id, { reminder_sent: true });
    } catch (e) {}
  };

  const todayAppts = appointments
    .filter((a) => isSameDay(a.appointment_datetime, today))
    .sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime));

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(today, i));

  return (
    <div className="space-y-6 animate-fade-up" data-testid="agenda-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Tu agenda</h1>
          <p className="text-sm text-slate-500 mt-1">Todo en un solo panel. Sin olvidos.</p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-full text-white gap-2"
          style={{ backgroundColor: "#275FBE" }}
          data-testid="add-appointment-button"
        >
          <Plus className="h-4 w-4" />
          Añadir cita
        </Button>
      </div>

      <div className="flex gap-2">
        <TabButton active={tab === "hoy"} onClick={() => setTab("hoy")} testid="tab-hoy">
          Hoy        </TabButton>
        <TabButton active={tab === "semana"} onClick={() => setTab("semana")} testid="tab-semana">
          Próximos 7 días
        </TabButton>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500" data-testid="risk-legend">
        <span className="font-medium text-slate-600">Riesgo de olvido:</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#DC2626" }} />
          Alto · ha faltado antes
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#D97706" }} />
          Medio · sin pagar o sin confirmar
        </span>
      </div>

      {tab === "hoy" ? (
        todayAppts.length === 0 ? (
          <EmptyState onAdd={openNew} />
        ) : (
          <div className="space-y-3" data-testid="appointments-list">
            {todayAppts.map((a) => (
              <AppointmentCard
                key={a.id}
                appt={a}
                therapists={therapists}
                risk={computeRisk(a, appointments)}
                onEdit={() => openEdit(a)}
                onDelete={() => handleDelete(a.id)}
                onTogglePayment={() => togglePayment(a)}
                onToggleConfirmed={() => toggleConfirmed(a)}
                onToggleNoShow={() => toggleNoShow(a)}
                onSendReminder={() => openReminder(a)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="space-y-6" data-testid="week-view">
          {weekDays.map((day) => {
            const dayAppts = appointments
              .filter((a) => isSameDay(a.appointment_datetime, day))
              .sort((a, b) => new Date(a.appointment_datetime) - new Date(b.appointment_datetime));
            return (
              <div key={day.toISOString()}>
                <h3 className="font-heading text-sm font-semibold text-slate-700 capitalize mb-2">
                  {formatDiaFecha(day.toISOString())}
                </h3>
                {dayAppts.length === 0 ? (
                  <p className="text-sm text-slate-400 pl-1">Sin citas</p>
                ) : (
                  <div className="space-y-3">
                    {dayAppts.map((a) => (
                      <AppointmentCard
                        key={a.id}
                        appt={a}
                        therapists={therapists}
                        risk={computeRisk(a, appointments)}
                        onEdit={() => openEdit(a)}
                        onDelete={() => handleDelete(a.id)}
                        onTogglePayment={() => togglePayment(a)}
                        onToggleConfirmed={() => toggleConfirmed(a)}
                        onToggleNoShow={() => toggleNoShow(a)}
                        onSendReminder={() => openReminder(a)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AppointmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        appointment={editing}
        therapists={therapists}
      />
      <ReminderMessageDialog
        open={!!reminder}
        appointment={reminder}
        onClose={() => setReminder(null)}
        onSent={(appt) => markSent(appt)}
      />
    </div>
  );
}

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
      active ? "text-white border-transparent" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
    }`}
    style={active ? { backgroundColor: "#275FBE" } : {}}
  >
    {children}
  </button>
);

const EmptyState = ({ onAdd }) => (
  <div className="py-16 text-center bg-white rounded-2xl border border-slate-200" data-testid="empty-state">
    <CalendarDays className="h-10 w-10 mx-auto text-slate-300" />
    <p className="mt-4 text-slate-600">
      Todavía no tienes citas aquí. Añade la primera cuando quieras.
    </p>
    <Button
      onClick={onAdd}
      className="mt-5 rounded-full text-white"
      style={{ backgroundColor: "#275FBE" }}
      data-testid="empty-add-button"
    >
      Añadir cita
    </Button>
  </div>
);
